import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";

import StandardTable from "../common/StandardTable";
import PageToolbar from "../common/PageToolbar";
import DateRangeFilter from "../common/DateRangeFilter";
import StatusChip from "../common/StatusChip";
import useDebounce from "../../hooks/useDebounce";

import { getLicenses } from "../../redux/actions/licenseActions";
import {
  generateRenewalInvoice,
  resetInvoiceServerStatus,
} from "../../redux/actions/invoiceActions";

const GenerateRenewalModal = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .split("T")[0];
  const nextThirtyDays = new Date(new Date().setDate(new Date().getDate() + 30))
    .toISOString()
    .split("T")[0];

  // Redux States
  const { licenses, pagination, loadingLicenses, errorLoadingLicenses } =
    useSelector((state) => state.license);
  const { loadingGenerateRenewal, serverStatus } = useSelector(
    (state) => state.invoice,
  );

  // Local Filters
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(nextThirtyDays);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 5 });

  const fetchLicensesList = useCallback(() => {
    dispatch(
      getLicenses({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (open) fetchLicensesList();
  }, [open, fetchLicensesList]);

  // Reset to page 1 on filter change
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  // Close modal on successful generation
  useEffect(() => {
    if (
      serverStatus?.operation === "generate" &&
      serverStatus?.status === 201
    ) {
      dispatch(resetInvoiceServerStatus());
      onClose(); // Auto-close modal
    }
  }, [serverStatus, dispatch, onClose]);

  const handleGenerate = (licenseId) => {
    dispatch(generateRenewalInvoice({ licenseId }));
  };

  const columns = useMemo(
    () => [
      {
        id: "licenseKey",
        label: "License Key",
        minWidth: 120,
        format: (val) => (
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", fontWeight: "bold" }}
          >
            {val}
          </Typography>
        ),
      },
      { id: "customerBusinessName", label: "Business Name", minWidth: 150 },
      { id: "productName", label: "Product", minWidth: 120 },
      {
        id: "expiryDate",
        label: "Expiry Date",
        minWidth: 100,
        format: (val) => new Date(val).toLocaleDateString(),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 100,
        format: (val) => <StatusChip status={val} />,
      },
    ],
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={loadingGenerateRenewal ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        Generate Renewal Invoice
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, minHeight: 400 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a license below to manually generate a renewal invoice. The
          system will automatically email the invoice to the customer if an
          email is configured.
        </Typography>

        <PageToolbar
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder="Search by license key or business name..."
        >
          <DateRangeFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </PageToolbar>

        <Box sx={{ mt: 2 }}>
          <StandardTable
            columns={columns}
            data={licenses}
            loading={loadingLicenses}
            error={errorLoadingLicenses}
            onRetry={fetchLicensesList}
            page={pageConfig.page}
            count={pagination?.totalElements || 0}
            rowsPerPage={pageConfig.limit}
            onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
            onRowsPerPageChange={(l) =>
              setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
            }
            enableColumnManagement={false}
            rowAction={(row) => (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={loadingGenerateRenewal}
                onClick={() => handleGenerate(row.id)}
              >
                {loadingGenerateRenewal ? "Generating..." : "Generate"}
              </Button>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loadingGenerateRenewal}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateRenewalModal;
